import { Prisma } from '@prisma/client'
import { useEffect } from 'react'
import { Api } from '~/core/trpc'
import { AppConstants } from '~/features/app/clientShared'
import { AppSessionType } from '~/features/appSession/common'
import { OrganizationType } from '~/features/organization/common'

const orderMap = new Map(
  AppConstants.KEYS_SORTED.map((name, index) => [name, index]),
)

export const useOrganizationMembers = (options: {
  organization: OrganizationType
  isAllowed: boolean
}) => {
  const { organization, isAllowed } = options

  const canFetch = !!organization && isAllowed

  const queryMembers = Api.organizationMember.findMany.useQuery(
    {
      where: { organizationId: organization?.id },
      include: {
        appSession: {
          include: {
            app: {
              select: {
                key: true,
                uiSettings: true,
                demoUrl: true,
              },
            },
          },
        },
        user: true,
      },
    },
    {
      initialData: [],
      enabled: canFetch,
    },
  )

  useEffect(() => {
    if (canFetch) {
      queryMembers.refetch()
    }
  }, [organization?.id])

  const isReady = isAllowed && (queryMembers.isFetched || !organization)

  const data = queryMembers.data.sort((member1, member2) => {
    const index1 = orderMap.get(member1.appSession?.app?.key) ?? Infinity
    const index2 = orderMap.get(member2.appSession?.app?.key) ?? Infinity

    return index1 - index2
  }) as OrganizationMemberFromContext[]

  return {
    ...queryMembers,
    data,
    isReady,
  }
}

export type OrganizationMemberFromContext =
  Prisma.OrganizationMemberGetPayload<{
    include: {
      appSession: {
        include: {
          app: { select: { key: true; uiSettings: true; demoUrl: true } }
        }
      }
      user: true
    }
  }> & { appSession?: AppSessionType }
